import {
  Box,
  Checkbox,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import _ from 'lodash';
import { OrgCandidateTypeIcon } from 'src/components/candidates/candidate-type.icon';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';
import { emptyRows, TableEmptyRows, TableHeadCustom, TableNoData, TableProps } from 'src/components/table';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import { useTranslate } from 'src/locales';
import { IOrgCandidate } from 'src/services/candidates/candidates.types';
import RecordPanel from 'src/components/record-panel/record-panel';
import { useRecordPanel } from 'src/components/record-panel/use-record-panel';

interface CandidateSearchResultsProps {
  candidates: IOrgCandidate[];
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  table: TableProps;
}

export default function CandidateSearchResults({
  candidates,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  table,
}: CandidateSearchResultsProps) {

  const { t } = useTranslate();
  const { openRecord } = useRecordPanel();

  const tableLabels = [
    { id: 'name', label: t('candidates.table.name') },
    { id: 'contact', label: t('candidates.table.contact_details') },
    { id: '', label: '' },
  ];

  const handleSelectAllRows = (checked: boolean) => {
    if (checked) {
      table.setSelected(candidates.map((row) => row.id));
      return;
    }

    table.setSelected([]);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    onPageChange(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  const denseHeight = 56 + 20;

  const onQuickView = (id: string) => openRecord('candidate', id, { mode: 'quick' });
  const onOpenOverlay = (id: string) => openRecord('candidate', id, { mode: 'overlay' });

  return (
    <>
    <Box>
      <TableContainer sx={{ overflow: 'unset' }}>
        <Scrollbar>
          <Table size="medium">
            <TableHeadCustom
              headLabel={tableLabels}
              rowCount={candidates.length}
              numSelected={table.selected.length}
              onSelectAllRows={handleSelectAllRows}
            />

            <TableBody>
              {candidates.map((row) => (
                <TableRow
                  hover
                  key={row.id}
                  selected={table.selected.includes(row.id)}
                  sx={{ cursor: 'pointer' }}
                >

                  {!!table.selected && (
                    <TableCell padding="checkbox">
                      <Checkbox checked={table ? table.selected.includes(row.id) : false} onClick={() => table.onSelectRow(row.id)} />
                    </TableCell>
                  )}

                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <OrgCandidateTypeIcon type={row.type} />
                      <Box>
                        <Link
                          component="button"
                          type="button"
                          noWrap
                          color="inherit"
                          underline="hover"
                          onClick={() => onOpenOverlay(row.id)}
                          sx={{ textAlign: 'left' }}
                        >
                          <TruncatedTextField
                            text={_.capitalize(`${row.candidate_profile.first_name} ${row.candidate_profile.last_name}`)}
                            mode="characters"
                            limit={50}
                            showMoreEnabled={false}
                            variant="subtitle2"
                            pb={0}
                            lineHeight={1}
                            style={{ textTransform: 'capitalize' }}
                          />
                        </Link>

                        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                          {row.candidate_profile.position_title || t('candidates.table.no_position_title')}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    {row.privileged_access ? (
                      <Stack direction="column" alignItems="start" justifyContent="start" spacing={0}>

                        <Link variant="body2" href={`mailto:${row.email}`} color="inherit">
                          {row.email}
                        </Link>

                        {
                          row.phone_number && (
                            <Link variant="caption" href={`tel:${row.phone_number}`} color="text.secondary">
                              {row.country_code} {row.phone_number}
                            </Link>
                          )
                        }

                      </Stack>
                    )
                      : (
                        <Label>
                          {t('common.via_recruiter')}
                        </Label>
                      )}
                  </TableCell>

                  <TableCell >
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                      <Tooltip title={t('common.quick_view')}>
                        <IconButton onClick={() => onQuickView(row.id)}>
                          <Iconify icon="eva:eye-outline" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>

                </TableRow>
              ))}

              <TableEmptyRows
                height={denseHeight}
                emptyRows={emptyRows(page - 1, rowsPerPage, total)}
              />

              <TableNoData notFound={candidates.length === 0} />
            </TableBody>
          </Table>
        </Scrollbar>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 250]}
      />
    </Box>

    <RecordPanel type="candidate" ids={candidates.map((c) => c.id)} total={total} />
    </>
  );
}